import { useState } from 'react'
import { CalendarDays, X } from 'lucide-react'
import { de, enUS } from 'react-day-picker/locale'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Button } from '@/components/shadcn/button'
import { Calendar } from '@/components/shadcn/calendar'

interface Props {
  value: Dayjs | null
  onChange: (value: Dayjs | null) => void
  disabled?: boolean
}

/**
 * Clearable single-date picker for the first execution. Empty = payout
 * inactive (the on/off switch of the schedule). Calendar locale reactive via
 * `i18n.resolvedLanguage`; the UTC normalization happens on save in
 * `StatusModuleSettingsCard`, not here.
 */
export function StatusFirstExecutionPicker({ value, onChange, disabled }: Props) {
  const { t, i18n } = useTranslation('service-modules-status')
  const [open, setOpen] = useState(false)
  const calendarLocale = i18n.resolvedLanguage?.startsWith('de') ? de : enUS
  const selected = value ? value.toDate() : undefined

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 gap-2 justify-start font-normal" disabled={disabled}>
            <CalendarDays className="size-4" />
            <span className={value ? 'font-medium' : 'text-muted-foreground'}>
              {value ? value.format('DD.MM.YYYY') : t('settings.firstExecutionPlaceholder')}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={calendarLocale}
            selected={selected}
            defaultMonth={selected}
            onSelect={(date) => {
              onChange(date ? dayjs(date) : null)
              setOpen(false)
            }}
            autoFocus
          />
        </PopoverContent>
      </Popover>
      {value && !disabled && (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={() => onChange(null)}
          title={t('settings.clearFirstExecution')}
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )
}
